import React, { useState, useEffect } from 'react';
import { MonthlyOpexData, OpexItem, OpexType, OpexRelationshipType } from '../types/opex';
// Exchange rate fixed at 1500 KRW per USD
import { opexApi } from '../services/opexApi';
import { convertMonthlyOpexToData } from '../utils/opexUtils';
import { useAlert } from '../contexts/AlertContext';

interface OpexDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data?: MonthlyOpexData;
  year: number;
  onUpdate: (updatedData: MonthlyOpexData) => Promise<MonthlyOpexData>;
  onConfirmSuccess?: () => void;
}

const OpexDetailModal: React.FC<OpexDetailModalProps> = ({ isOpen, onClose, data, year, onUpdate, onConfirmSuccess }) => {
  const { showToast, showConfirm } = useAlert();
  const [editMode, setEditMode] = useState(false);
  const [localData, setLocalData] = useState<MonthlyOpexData | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const FIXED_EXCHANGE_RATE = 1500;
  const [validationErrors, setValidationErrors] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (data) {
      setLocalData(JSON.parse(JSON.stringify(data))); // Deep copy
      setEditMode(false); // Always start in read mode
    }
  }, [data]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);


  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('ko-KR').format(Math.round(num));
  };

  // 선택된 통화에 따라 금액을 포맷하는 함수
  const formatAmountByCurrency = (item: OpexItem) => {
    if (item.currency === 'USD' && item.originalAmount) {
      return {
        primary: `$${formatNumber(item.originalAmount)}`,
        secondary: `₩${formatCurrency(item.amount)}`,
        currencySymbol: '$'
      };
    } else {
      return {
        primary: `₩${formatCurrency(item.amount)}`,
        secondary: item.currency === 'USD' && item.originalAmount ? `$${formatNumber(item.originalAmount)}` : null,
        currencySymbol: '₩'
      };
    }
  };

  // 계층형 구조를 고려한 재귀적 합계 계산 함수 (3단계 지원)
  const calculateHierarchicalTotal = (items: OpexItem[]): { krw: number; usd: number; hasUsd: boolean } => {
    let krwTotal = 0;
    let usdTotal = 0;
    let hasUsdItems = false;

    const calculateRecursive = (itemList: OpexItem[]) => {
      itemList.forEach(item => {
        if (item.children && item.children.length > 0) {
          // 카테고리인 경우 자식 항목들을 재귀적으로 계산
          calculateRecursive(item.children);
        } else {
          // 일반 항목인 경우 금액을 합계에 추가
          krwTotal += item.amount || 0;
          
          if (item.currency === 'USD' && item.originalAmount) {
            usdTotal += item.originalAmount;
            hasUsdItems = true;
          }
        }
      });
    };

    calculateRecursive(items);
    return { krw: krwTotal, usd: usdTotal, hasUsd: hasUsdItems };
  };

  // 합계 금액에 대한 다중 통화 표시 함수
  const formatTotalWithCurrencies = (items: OpexItem[]) => {
    const totals = calculateHierarchicalTotal(items);
    
    return {
      krw: totals.krw,
      usd: totals.usd,
      hasUsdItems: totals.hasUsd,
      primary: `₩${formatCurrency(totals.krw)}`,
      secondary: totals.hasUsd && totals.usd > 0 ? `$${formatNumber(totals.usd)}` : null
    };
  };


  const extractNumericValue = (value: string): number => {
    // 숫자만 추출하고 정수로 변환
    const numericValue = value.replace(/[^\d.]/g, '');
    return numericValue ? parseFloat(numericValue) : 0;
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  const updateItemCurrency = (id: number, currency: 'KRW' | 'USD', isDirect: boolean) => {
    if (!localData) return;
    
    const targetArray = isDirect ? directItems : indirectItems;
    
    // 계층형 구조를 고려한 통화 업데이트 함수
    const findAndUpdateCurrency = (items: OpexItem[]): OpexItem[] => {
      return items.map(item => {
        // 현재 항목이 대상인지 확인
        if (item.id === id) {
          // 통화 변경 시 금액 변환
          if (item.currency !== currency) {
            if (currency === 'USD' && item.currency === 'KRW') {
              // KRW -> USD 변환
              const usdAmount = item.amount / FIXED_EXCHANGE_RATE;
              return {
                ...item,
                currency: 'USD' as const,
                originalAmount: usdAmount,
                exchangeRate: FIXED_EXCHANGE_RATE
              };
            } else if (currency === 'KRW' && item.currency === 'USD') {
              // USD -> KRW 변환  
              return {
                ...item,
                currency: 'KRW' as const,
                originalAmount: item.amount,
                exchangeRate: undefined
              };
            }
          }
          return item;
        }
        
        // 자식 항목들도 재귀적으로 확인
        if (item.children && item.children.length > 0) {
          const updatedChildren = findAndUpdateCurrency(item.children);
          return {
            ...item,
            children: updatedChildren
          };
        }
        
        return item;
      });
    };
    
    const updatedArray = findAndUpdateCurrency(targetArray);
    
    setLocalData({
      ...localData,
      [isDirect ? 'direct' : 'indirect']: updatedArray
    });
  };

  const updateItemAmount = (id: number, value: string, currency: 'KRW' | 'USD', isDirect: boolean) => {
    if (!localData) return;
    
    const targetArray = isDirect ? directItems : indirectItems;
    
    // 계층형 구조를 고려한 금액 업데이트 함수
    const findAndUpdateAmount = (items: OpexItem[]): OpexItem[] => {
      return items.map(item => {
        // 현재 항목이 대상인지 확인
        if (item.id === id) {
          const numericValue = extractNumericValue(value);
          
          if (currency === 'USD') {
            // USD 입력 시 KRW로 변환하여 amount에 저장
            return {
              ...item,
              originalAmount: numericValue,
              amount: Math.round(numericValue * FIXED_EXCHANGE_RATE),
              currency: 'USD',
              exchangeRate: FIXED_EXCHANGE_RATE
            };
          } else {
            // KRW 입력
            return {
              ...item,
              amount: Math.round(numericValue),
              originalAmount: Math.round(numericValue),
              currency: 'KRW',
              exchangeRate: undefined
            };
          }
        }
        
        // 자식 항목들도 재귀적으로 확인
        if (item.children && item.children.length > 0) {
          const updatedChildren = findAndUpdateAmount(item.children);
          return {
            ...item,
            children: updatedChildren
          };
        }
        
        return item;
      });
    };
    
    const updatedArray = findAndUpdateAmount(targetArray);
    
    const newLocalData = {
      ...localData,
      [isDirect ? 'direct' : 'indirect']: updatedArray
    };
    
    setLocalData(newLocalData);
    
    // 실시간 유효성 검사 - 편집 중에 오류 표시 업데이트
    if (editMode) {
      const allItems = [...(newLocalData.indirect || []), ...(newLocalData.direct || [])];
      const validation = validateOpexData(allItems);
      setValidationErrors(validation.invalidIds);
    }
  };

  const updateItem = (id: number, field: keyof OpexItem, value: string | number, isDirect: boolean) => {
    if (!localData) return;
    
    const targetArray = isDirect ? directItems : indirectItems;
    
    // 계층형 구조를 고려한 항목 찾기 및 업데이트 함수
    const findAndUpdateItem = (items: OpexItem[]): OpexItem[] => {
      return items.map(item => {
        // 현재 항목이 대상인지 확인
        if (item.id === id) {
          const updatedItem = { ...item };
          
          if (field === 'amount') {
            // amount 필드는 항상 숫자로 저장
            const numericAmount = typeof value === 'string' ? extractNumericValue(value) : value;
            updatedItem[field] = numericAmount;
          } else if (field === 'quantity') {
            // 수량 업데이트 시 총액 자동 계산
            updatedItem.quantity = typeof value === 'number' ? value : parseInt(value as string) || 0;
            if (updatedItem.quantity !== undefined && updatedItem.unitPrice !== undefined) {
              const calculatedAmount = updatedItem.quantity * updatedItem.unitPrice;
              updatedItem.amount = updatedItem.currency === 'USD' ? calculatedAmount * FIXED_EXCHANGE_RATE : calculatedAmount;
              updatedItem.originalAmount = calculatedAmount;
            }
          } else {
            // 다른 필드는 그대로 저장
            (updatedItem as any)[field] = value;
          }
          
          return updatedItem;
        }
        
        // 자식 항목들도 재귀적으로 확인
        if (item.children && item.children.length > 0) {
          const updatedChildren = findAndUpdateItem(item.children);
          return {
            ...item,
            children: updatedChildren
          };
        }
        
        return item;
      });
    };
    
    const updatedArray = findAndUpdateItem(targetArray);
    
    const newLocalData = {
      ...localData,
      [isDirect ? 'direct' : 'indirect']: updatedArray
    };
    
    setLocalData(newLocalData);
    
    // 실시간 유효성 검사 - 편집 중에 오류 표시 업데이트
    if (editMode) {
      const allItems = [...(newLocalData.indirect || []), ...(newLocalData.direct || [])];
      const validation = validateOpexData(allItems);
      setValidationErrors(validation.invalidIds);
    }
  };

  // 단가 업데이트 함수
  const updateItemUnitPrice = (id: number, value: string, currency: 'KRW' | 'USD', isDirect: boolean) => {
    if (!localData) return;
    
    const targetArray = isDirect ? directItems : indirectItems;
    
    const findAndUpdateUnitPrice = (items: OpexItem[]): OpexItem[] => {
      return items.map(item => {
        if (item.id === id) {
          const numericValue = extractNumericValue(value);
          const updatedItem = { ...item };
          
          updatedItem.unitPrice = numericValue;
          updatedItem.currency = currency;
          
          // 수량과 단가가 모두 정의되어 있으면 총액 자동 계산
          if (updatedItem.quantity !== undefined && updatedItem.unitPrice !== undefined) {
            const calculatedAmount = updatedItem.quantity * updatedItem.unitPrice;
            
            if (currency === 'USD') {
              updatedItem.amount = Math.round(calculatedAmount * FIXED_EXCHANGE_RATE);
              updatedItem.originalAmount = calculatedAmount;
              updatedItem.exchangeRate = FIXED_EXCHANGE_RATE;
            } else {
              updatedItem.amount = Math.round(calculatedAmount);
              updatedItem.originalAmount = Math.round(calculatedAmount);
            }
          }
          
          return updatedItem;
        }
        
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: findAndUpdateUnitPrice(item.children)
          };
        }
        
        return item;
      });
    };
    
    const updatedArray = findAndUpdateUnitPrice(targetArray);
    
    const newLocalData = {
      ...localData,
      [isDirect ? 'direct' : 'indirect']: updatedArray
    };
    
    setLocalData(newLocalData);
    
    if (editMode) {
      const allItems = [...(newLocalData.indirect || []), ...(newLocalData.direct || [])];
      const validation = validateOpexData(allItems);
      setValidationErrors(validation.invalidIds);
    }
  };

  const addRow = (type: 'indirect' | 'direct', isCategory: boolean = false) => {
    if (!localData) return;
    
    // 임시 ID 생성 (음수로 설정하여 새 항목임을 명확히 구분)
    const existingIds = [
      ...indirectItems.map(item => item.id),
      ...directItems.map(item => item.id)
    ];
    
    // 기존 임시 ID들 중 가장 작은 음수 찾기
    const existingTempIds = existingIds.filter(id => id < 0);
    const nextTempId = existingTempIds.length > 0 ? Math.min(...existingTempIds) - 1 : -1;
    
    // NO 필드는 백엔드에서 자동 생성됨 (계층형 구조에 따라)
    
    
    const newItem: OpexItem = {
      id: nextTempId, // 음수 임시 ID 사용
      no: undefined, // NO 필드는 백엔드에서 생성
      category: '',  // 빈 문자열로 시작하여 바로 입력 가능하게
      amount: isCategory ? 0 : 100000, // 카테고리는 0, 일반 항목은 기본값 10만원
      originalAmount: isCategory ? 0 : 100000,
      currency: 'KRW',
      note: '',
      type: type === 'indirect' ? OpexType.INDIRECT : OpexType.DIRECT,
      relationshipType: OpexRelationshipType.MONTHLY_OPEX,
      createdAt: new Date(),
      updatedAt: new Date(),
      // 계층형 구조 필드
      parentId: undefined,
      level: 0,
      isCategory: isCategory,
      children: isCategory ? [] : undefined,
      quantity: isCategory ? undefined : 1, // 카테고리가 아닌 경우 기본 수량 1
      unitPrice: isCategory ? undefined : 0 // 카테고리가 아닌 경우 기본 단가 0
    };
    
    setLocalData({
      ...localData,
      [type]: [...(localData[type] || []), newItem]
    });
    
    // 새로 추가된 항목에 포커싱
    setTimeout(() => {
      const input = document.querySelector(`input[data-item-id="${nextTempId}"]`) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 100);
    
  };


  const addChildItem = (parentId: number, type: 'indirect' | 'direct', isSubCategory: boolean = false) => {
    if (!localData) return;
    
    const targetArray = type === 'direct' ? directItems : indirectItems;
    
    // 부모 항목을 찾기 (최상위 또는 하위에서)
    let parentItem: OpexItem | undefined;
    let grandParentItem: OpexItem | undefined;
    
    // 최상위에서 찾기
    parentItem = targetArray.find(item => item.id === parentId);
    
    // 최상위에서 못 찾으면 children에서 찾기
    if (!parentItem) {
      for (const topItem of targetArray) {
        if (topItem.children) {
          const childItem = topItem.children.find(child => child.id === parentId);
          if (childItem) {
            parentItem = childItem;
            grandParentItem = topItem;
            break;
          }
        }
      }
    }
    
    if (!parentItem) {
      return;
    }
    
    // 임시 ID 생성 (음수로 설정하여 새 항목임을 명확히 구분)
    const existingIds = [
      ...indirectItems.map(item => item.id),
      ...directItems.map(item => item.id),
      // children의 ID들도 포함
      ...indirectItems.flatMap(item => (item.children || []).map(child => child.id)),
      ...directItems.flatMap(item => (item.children || []).map(child => child.id)),
      // grandchildren의 ID들도 포함
      ...indirectItems.flatMap(item => 
        (item.children || []).flatMap(child => (child.children || []).map(grandchild => grandchild.id))
      ),
      ...directItems.flatMap(item => 
        (item.children || []).flatMap(child => (child.children || []).map(grandchild => grandchild.id))
      )
    ];
    
    // 기존 임시 ID들 중 가장 작은 음수 찾기
    const existingTempIds = existingIds.filter(id => id < 0);
    const childId = existingTempIds.length > 0 ? Math.min(...existingTempIds) - 1 : -1;
    
    // NO 필드는 백엔드에서 자동 생성됨 (계층형 구조에 따라)
    
    // 부모의 level에 따라 새 항목의 level 결정
    const newLevel = (parentItem.level || 0) + 1;
    
    const newChild: OpexItem = {
      id: childId,
      no: undefined, // NO 필드는 백엔드에서 생성
      category: '',
      amount: isSubCategory ? 0 : 100000, // 서브카테고리는 0, 일반 항목은 기본값 10만원
      originalAmount: isSubCategory ? 0 : 100000,
      currency: 'KRW',
      exchangeRate: FIXED_EXCHANGE_RATE,
      note: '',
      type: type === 'direct' ? OpexType.DIRECT : OpexType.INDIRECT,
      relationshipType: OpexRelationshipType.MONTHLY_OPEX,
      createdAt: new Date(),
      updatedAt: new Date(),
      parentId: parentId,
      level: newLevel,
      isCategory: isSubCategory,
      children: isSubCategory ? [] : undefined,
      quantity: isSubCategory ? undefined : 1, // 서브카테고리가 아닌 경우 기본 수량 1
      unitPrice: isSubCategory ? undefined : 100000 // 서브카테고리가 아닌 경우 기본 단가 10만원
    };
    
    // 부모 카테고리의 children 배열에 새 항목 추가
    const updatedParent = {
      ...parentItem,
      children: [...(parentItem.children || []), newChild]
    };
    
    let updatedArray;
    if (grandParentItem) {
      // 3단계: grandParent > parent > newChild
      const updatedGrandParent = {
        ...grandParentItem,
        children: grandParentItem.children?.map(child => 
          child.id === parentId ? updatedParent : child
        ) || []
      };
      
      updatedArray = targetArray.map(item => 
        item.id === grandParentItem!.id ? updatedGrandParent : item
      );
    } else {
      // 2단계: parent > newChild
      updatedArray = targetArray.map(item => 
        item.id === parentId ? updatedParent : item
      );
    }
    
    setLocalData({
      ...localData,
      [type]: updatedArray
    });
    
    // 새로 추가된 하위 항목에 포커싱
    setTimeout(() => {
      const input = document.querySelector(`input[data-item-id="${childId}"]`) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 100);
    
  };

  const deleteRow = async (id: number, isDirect: boolean) => {
    if (!localData) return;
    
    const targetArray = isDirect ? directItems : indirectItems;
    
    // 3단계 구조에서 항목 찾기
    let itemToDelete: OpexItem | undefined;
    let parentItem: OpexItem | undefined;
    let grandParentItem: OpexItem | undefined;
    
    // 최상위에서 찾기
    itemToDelete = targetArray.find(item => item.id === id);
    
    // Level 2에서 찾기
    if (!itemToDelete) {
      for (const topItem of targetArray) {
        if (topItem.children) {
          const level2Item = topItem.children.find(child => child.id === id);
          if (level2Item) {
            itemToDelete = level2Item;
            parentItem = topItem;
            break;
          }
          
          // Level 3에서 찾기
          for (const level2Item of topItem.children) {
            if (level2Item.children) {
              const level3Item = level2Item.children.find(grandchild => grandchild.id === id);
              if (level3Item) {
                itemToDelete = level3Item;
                parentItem = level2Item;
                grandParentItem = topItem;
                break;
              }
            }
          }
          if (itemToDelete) break;
        }
      }
    }
    
    if (!itemToDelete) {
      return;
    }
    
    // 삭제 확인 메시지 (카테고리인 경우 하위 항목 포함 안내)
    let confirmMessage = `"${itemToDelete.category}" 항목을 삭제하시겠습니까?`;
    if (itemToDelete.isCategory && itemToDelete.children && itemToDelete.children.length > 0) {
      confirmMessage += `\n\n⚠️ 카테고리를 삭제하면 하위 ${itemToDelete.children.length}개 항목도 함께 삭제됩니다.`;
    }

    const confirmed = await showConfirm(confirmMessage);
    if (!confirmed) return;
    
    // 로컬 상태에서 항목 삭제 (3단계 구조 고려)
    const deleteFromLocalState = () => {
      if (grandParentItem && parentItem) {
        // Level 3 항목 삭제: 부모(Level 2)의 children 배열에서 제거
        const updatedParent = {
          ...parentItem,
          children: parentItem.children?.filter(child => child.id !== id) || []
        };
        
        const updatedGrandParent = {
          ...grandParentItem,
          children: grandParentItem.children?.map(child => 
            child.id === parentItem!.id ? updatedParent : child
          ) || []
        };
        
        const updatedArray = targetArray.map(item => 
          item.id === grandParentItem!.id ? updatedGrandParent : item
        );
        
        setLocalData({
          ...localData,
          [isDirect ? 'direct' : 'indirect']: updatedArray
        });
      } else if (parentItem) {
        // Level 2 항목 삭제: 부모(Level 1)의 children 배열에서 제거
        const updatedParent = {
          ...parentItem,
          children: parentItem.children?.filter(child => child.id !== id) || []
        };
        
        const updatedArray = targetArray.map(item => 
          item.id === parentItem!.id ? updatedParent : item
        );
        
        setLocalData({
          ...localData,
          [isDirect ? 'direct' : 'indirect']: updatedArray
        });
      } else {
        // Level 1 최상위 항목 삭제
        const filteredArray = targetArray.filter(item => item.id !== id);
        setLocalData({
          ...localData,
          [isDirect ? 'direct' : 'indirect']: filteredArray
        });
      }
    };

    // 새로 추가된 항목(음수 ID)인 경우 로컬에서만 삭제
    if (id < 0) {
      deleteFromLocalState();
      return;
    }
    
    // 기존 DB 항목인 경우 즉시 DB에서 삭제
    try {
      await opexApi.deleteOpexItem(id);
      
      // 삭제 성공 시 로컬 상태에서도 제거
      deleteFromLocalState();
      
    } catch (error) {
      showToast('항목 삭제에 실패했습니다. 다시 시도해 주세요.', 'error');
    }
  };

  const handleConfirm = async () => {
    if (!localData) return;
    
    try {
      let dataToConfirm = localData;
      
      // 편집 모드인 경우 먼저 저장하고 저장된 데이터를 사용
      if (editMode) {
        // 확정 전 유효성 검사
        const allItems = [...(localData.indirect || []), ...(localData.direct || [])];
        const validation = validateOpexData(allItems);
        
        if (validation.errors.length > 0) {
          setValidationErrors(validation.invalidIds);
          showToast('확정하기 전에 항목들을 확인해주세요.', 'warning');
          return;
        }
        
        // 유효성 검사 통과 시 오류 상태 초기화
        setValidationErrors(new Set());
        
        // 계층형 데이터를 평면화
        const flattenedIndirect = flattenHierarchicalData(localData.indirect || []);
        const flattenedDirect = flattenHierarchicalData(localData.direct || []);
        
        // 평면화된 데이터로 저장
        const dataToSave = {
          ...localData,
          indirect: flattenedIndirect,
          direct: flattenedDirect
        };
        
        dataToConfirm = await onUpdate(dataToSave);
        setLocalData(dataToConfirm);
        setEditMode(false);
        
      }
      
      // OPEX 항목이 하나라도 있는지 확인
      const hasOpexItems = (dataToConfirm.indirect?.length || 0) > 0 || (dataToConfirm.direct?.length || 0) > 0;
      
      if (!hasOpexItems) {
        return;
      }
      
      
      // 백엔드 확정 API 호출
      const confirmResponse = await opexApi.confirmMonth(year, dataToConfirm.month);
      
      // 확정된 월 데이터 업데이트
      let confirmedData = localData;
      if (confirmResponse.confirmedMonth) {
        // 백엔드에서 확정된 월 데이터를 반환한 경우
        confirmedData = convertMonthlyOpexToData(confirmResponse.confirmedMonth);
      } else {
        // 기존 데이터를 확정 상태로 변경
        confirmedData = {
          ...localData,
          confirmed: true
        };
      }
      
      setLocalData(confirmedData);
      setEditMode(false);
      
      // 전체 데이터 새로고침 (확정으로 인해 다른 월 데이터도 변경될 수 있음)
      // 모달 닫기 전에 먼저 데이터 새로고침
      if (onConfirmSuccess) {
        await onConfirmSuccess();
      }
      
      // 모달 닫기
      onClose();
      
    } catch (error) {
      // 확정 처리 실패 처리
    }
  };

  const handleUnconfirm = async () => {
    if (!localData) return;

    // 사용자 확인
    const userConfirmed = await showConfirm(
      `${localData.month}월 확정을 해제하시겠습니까?\n\n확정 해제 시 해당 월이 계획 상태로 변경됩니다.`
    );

    if (!userConfirmed) return;

    try {
      // 백엔드 확정해제 API 호출
      const unconfirmResponse = await opexApi.unconfirmMonth(year, localData.month);

      // 로컬 데이터를 계획 상태로 변경
      const unconfirmedData = {
        ...localData,
        confirmed: false
      };

      setLocalData(unconfirmedData);
      setEditMode(false);

      // 성공 알림
      showToast(`${localData.month}월 확정이 해제되어 계획 상태로 변경되었습니다.`, 'success');

      // 전체 데이터 새로고침
      if (onConfirmSuccess) {
        await onConfirmSuccess();
      }

    } catch (error) {
      showToast('확정 해제 처리에 실패했습니다. 다시 시도해 주세요.', 'error');
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    // 편집 모드 시작 시 유효성 검사 상태 초기화
    setValidationErrors(new Set());
  };

  // 계층형 구조를 평면화하여 저장용 데이터로 변환 (3단계 지원)
  const flattenHierarchicalData = (items: OpexItem[]): OpexItem[] => {
    const flattened: OpexItem[] = [];
    
    const flattenRecursive = (itemList: OpexItem[], parentId?: number) => {
      itemList.forEach(item => {
        // 현재 항목 추가 (children 제거, NO 필드 보존)
        flattened.push({
          ...item,
          parentId: parentId || item.parentId,
          children: undefined, // children은 제거하고 저장
          no: item.no // NO 필드 보존
        });
        
        // 자식 항목들을 재귀적으로 처리
        if (item.children && item.children.length > 0) {
          flattenRecursive(item.children, item.id);
        }
      });
    };
    
    flattenRecursive(items);
    return flattened;
  };

  // 유효성 검사 함수 (3단계 구조 지원)
  const validateOpexData = (items: OpexItem[]): { errors: string[]; invalidIds: Set<number> } => {
    const errors: string[] = [];
    const invalidIds = new Set<number>();
    
    const validateRecursive = (itemList: OpexItem[], typeLabel: string, parentPath: string = '') => {
      itemList.forEach((item, index) => {
        const currentPath = parentPath ? `${parentPath} > ${item.category || '이름 없음'}` : `${typeLabel} ${index + 1}번째 항목`;
        
        // 카테고리 이름 검사
        if (!item.category || !item.category.trim()) {
          errors.push(`${currentPath}: 카테고리명을 입력해주세요.`);
          invalidIds.add(item.id);
        }
        
        // 일반 항목(카테고리가 아닌 경우)의 금액 검사 (0 이상 허용)
        if (!item.isCategory && (item.amount === undefined || item.amount === null || item.amount < 0)) {
          errors.push(`${currentPath}: 금액을 입력해주세요.`);
          invalidIds.add(item.id);
        }
        
        // 자식 항목들을 재귀적으로 검사
        if (item.children && item.children.length > 0) {
          validateRecursive(item.children, typeLabel, currentPath);
        }
      });
    };
    
    validateRecursive(items.filter(item => item.type === 'indirect'), 'Indirect');
    validateRecursive(items.filter(item => item.type === 'direct'), 'Direct');
    
    return { errors, invalidIds };
  };

  const handleSave = async () => {
    if (localData) {
      try {
        // 저장 전 유효성 검사
        const allItems = [...(localData.indirect || []), ...(localData.direct || [])];
        const validation = validateOpexData(allItems);
        
        if (validation.errors.length > 0) {
          setValidationErrors(validation.invalidIds);
          showToast('항목들을 확인해주세요.', 'warning');
          return;
        }
        
        // 유효성 검사 통과 시 오류 상태 초기화
        setValidationErrors(new Set());
        
        // 계층형 데이터를 평면화
        const flattenedIndirect = flattenHierarchicalData(localData.indirect || []);
        const flattenedDirect = flattenHierarchicalData(localData.direct || []);
        
        
        // 평면화된 데이터로 저장
        const dataToSave = {
          ...localData,
          indirect: flattenedIndirect,
          direct: flattenedDirect
        };
        
        
        const savedData = await onUpdate(dataToSave);
        
        
        setLocalData(savedData);
        setEditMode(false);
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '저장에 실패했습니다.';
        showToast(errorMessage, 'error');
      }
    }
  };

  if (!isOpen || !localData) return null;

  // Defensive checks for data structure
  const indirectItems = localData.indirect || [];
  const directItems = localData.direct || [];
  
  // 계층형 구조를 고려한 정확한 합계 계산
  const calculateSubtotal = (items: OpexItem[]) => {
    return calculateHierarchicalTotal(items).krw;
  };

  const indirectTotal = calculateSubtotal(indirectItems);
  const directTotal = calculateSubtotal(directItems);
  const total = indirectTotal + directTotal;

  const renderHierarchicalItems = (items: OpexItem[], isDirect: boolean) => {
    const rows: React.ReactElement[] = [];
    let globalIndex = 0;

    // 3단계 구조를 위한 재귀적 합계 계산 함수
    const calculateHierarchicalTotal = (item: OpexItem): number => {
      if (item.children && item.children.length > 0) {
        return item.children.reduce((sum, child) => {
          return sum + calculateHierarchicalTotal(child);
        }, 0);
      } else {
        return Number(item.amount || 0);
      }
    };

    items.forEach((item, itemIndex) => {
      // 카테고리인 경우 (isCategory가 true이거나 children 배열이 있는 경우)
      if (item.isCategory || item.children) {
        // 카테고리 헤더 (소계 표시) - 3단계 구조 지원
        const categoryTotal = calculateHierarchicalTotal(item);
        const categoryPercentage = total > 0 ? (categoryTotal / total * 100).toFixed(1) + '%' : '0.0%';
        
        const isNewCategory = item.id < 0; // 새로 추가된 카테고리인지 확인
        const categoryBgClass = isNewCategory ? "bg-blue-50 border-b border-blue-200" : "bg-white border-b border-gray-200";
        const categoryTextClass = isNewCategory ? "text-blue-600" : "text-gray-600";
        
        rows.push(
          <tr key={`category-${item.id}`} className={categoryBgClass}>
            <td className="px-4 py-3 text-center border-l-4 border-blue-500 hidden">
              <span className={`text-sm font-medium ${categoryTextClass}`}>
                {globalIndex + 1}
              </span>
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center justify-between">
                <input
                  type="text"
                  value={item.category}
                  onChange={(e) => updateItem(item.id, 'category', e.target.value, isDirect)}
                  className={`font-medium ${isNewCategory ? 'text-blue-700 bg-blue-50' : 'text-gray-700 bg-white'} ${!editMode ? 'border-transparent cursor-default' : validationErrors.has(item.id) ? 'border border-red-500 focus:border-red-600' : isNewCategory ? 'border border-blue-200 focus:border-blue-500' : 'border border-gray-200 focus:border-gray-500'} outline-none rounded px-2 py-1 text-sm`}
                  readOnly={!editMode}
                  placeholder={editMode ? "카테고리명을 입력하세요" : ""}
                  data-item-id={item.id}
                />
                {editMode && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => addChildItem(item.id, isDirect ? 'direct' : 'indirect', false)}
                      className="text-green-600 hover:text-green-800 w-6 h-6 flex items-center justify-center rounded hover:bg-green-50"
                      title={`"${item.category}" 카테고리에 항목 추가`}
                    >
                      +
                    </button>
                    <button
                      onClick={() => deleteRow(item.id, isDirect)}
                      className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 w-7 h-7 flex items-center justify-center rounded"
                      title={`"${item.category}" 카테고리 삭제`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </td>
            <td className="px-4 py-3 text-center">
              {/* 카테고리는 수량 없음 */}
              <span className="text-slate-400">-</span>
            </td>
            <td className="px-4 py-3 text-center">
              {/* 카테고리는 단가 없음 */}
              <span className="text-slate-400">-</span>
            </td>
            <td className="px-4 py-3 text-right">
              <div className={`font-semibold ${isNewCategory ? 'text-blue-700' : 'text-gray-700'}`}>
                {formatCurrency(categoryTotal)}
              </div>
            </td>
            <td className={`px-4 py-3 text-right text-sm ${isNewCategory ? 'text-blue-600' : 'text-gray-600'}`}>
              <span className="font-medium">
                ${formatNumber(categoryTotal / FIXED_EXCHANGE_RATE)}
              </span>
            </td>
            <td className={`px-4 py-3 text-right text-sm font-semibold ${isNewCategory ? 'text-blue-600' : 'text-gray-600'}`}>{categoryPercentage}</td>
            <td className="px-4 py-3">
              <span className={`text-sm ${isNewCategory ? 'text-blue-600' : 'text-gray-600'}`}>소계</span>
            </td>
          </tr>
        );
        globalIndex++;

        // 자식 항목들 - 3단계 구조 지원
        (item.children || []).forEach((child, childIndex) => {
          // Level 2 서브카테고리인 경우
          if (child.isCategory || child.children) {
            const subcategoryTotal = calculateHierarchicalTotal(child);
            const subcategoryPercentage = total > 0 ? (subcategoryTotal / total * 100).toFixed(1) + '%' : '0.0%';
            
            rows.push(
              <tr key={child.id} className="border-b border-slate-100 bg-blue-25">
                <td className="px-4 py-3 text-center border-l-4 border-green-500 hidden">
                  <span className="text-sm font-medium text-blue-600">
                    {`${itemIndex + 1}.${childIndex + 1}`}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <span className="text-blue-400 mr-2">└</span>
                    <input
                      type="text"
                      value={child.category}
                      onChange={(e) => updateItem(child.id, 'category', e.target.value, isDirect)}
                      className={`flex-1 text-left px-3 py-2 rounded text-sm font-medium text-blue-700 ${!editMode ? 'bg-transparent cursor-default border-transparent' : validationErrors.has(child.id) ? 'bg-white border border-red-500 focus:border-red-600 outline-none' : 'bg-blue-50 border border-blue-200 focus:border-blue-500 outline-none'}`}
                      readOnly={!editMode}
                      placeholder={editMode ? "서브카테고리명을 입력하세요" : ""}
                      data-item-id={child.id}
                    />
                    {editMode && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => addChildItem(child.id, isDirect ? 'direct' : 'indirect', false)}
                          className="text-green-600 hover:text-green-800 w-6 h-6 flex items-center justify-center rounded hover:bg-green-50"
                          title={`"${child.category}" 서브카테고리에 항목 추가`}
                        >
                          +
                        </button>
                        <button
                          onClick={() => deleteRow(child.id, isDirect)}
                          className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 w-7 h-7 flex items-center justify-center rounded"
                          title={`"${child.category}" 서브카테고리 삭제`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="text-slate-400">-</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="text-slate-400">-</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="font-semibold text-blue-700">
                    {formatCurrency(subcategoryTotal)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-sm text-blue-600">
                  <span className="font-medium">
                    ${formatNumber(subcategoryTotal / FIXED_EXCHANGE_RATE)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-sm font-semibold text-blue-600">{subcategoryPercentage}</td>
                <td className="px-4 py-3">
                  <span className="text-sm text-blue-600">소계</span>
                </td>
              </tr>
            );
            
            // Level 3 세부 항목들
            (child.children || []).forEach((grandchild, grandchildIndex) => {
              const itemPercentage = total > 0 ? (Number(grandchild.amount || 0) / total * 100).toFixed(1) + '%' : '0.0%';
              const usdAmount = grandchild.amount / FIXED_EXCHANGE_RATE;
              
              rows.push(
                <tr key={grandchild.id} className="border-b border-slate-50 bg-slate-25">
                  <td className="px-4 py-3 text-center border-l-2 border-gray-400 hidden">
                    <span className="text-sm font-medium text-slate-500">
                      {`${itemIndex + 1}.${childIndex + 1}.${grandchildIndex + 1}`}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <span className="text-slate-300 mr-1">└</span>
                      <span className="text-slate-300 mr-2">└</span>
                      <input
                        type="text"
                        value={grandchild.category}
                        onChange={(e) => updateItem(grandchild.id, 'category', e.target.value, isDirect)}
                        className={`flex-1 text-left px-3 py-2 rounded text-sm ${!editMode ? 'bg-transparent cursor-default border-transparent' : validationErrors.has(grandchild.id) ? 'bg-white border border-red-500 focus:border-red-600 outline-none' : 'bg-white border border-slate-300 focus:border-indigo-500 outline-none'}`}
                        readOnly={!editMode}
                        placeholder={editMode ? "항목명을 입력하세요" : ""}
                        data-item-id={grandchild.id}
                      />
                      {editMode && (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => deleteRow(grandchild.id, isDirect)}
                            className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 w-7 h-7 flex items-center justify-center rounded"
                            title={`"${grandchild.category}" 항목 삭제`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  {/* Level 3 항목들의 수량, 단가, 금액 등 */}
                  <td className="px-4 py-3 text-center">
                    {editMode ? (
                      <input
                        type="number"
                        value={grandchild.quantity || ''}
                        onChange={(e) => updateItem(grandchild.id, 'quantity', parseInt(e.target.value) || 0, isDirect)}
                        className="w-16 text-center px-2 py-1 rounded text-sm border border-slate-300 focus:border-indigo-500 outline-none"
                        placeholder="1"
                        min="0"
                      />
                    ) : (
                      <span className="text-sm font-medium">{grandchild.quantity || '-'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editMode ? (
                      <div className="flex items-center space-x-1">
                        <select
                          value={grandchild.currency || 'KRW'}
                          onChange={(e) => updateItemCurrency(grandchild.id, e.target.value as 'KRW' | 'USD', isDirect)}
                          className="text-xs border rounded px-1 py-1"
                        >
                          <option value="KRW">₩</option>
                          <option value="USD">$</option>
                        </select>
                        <input
                          type="text"
                          value={grandchild.currency === 'USD' ? formatNumber(grandchild.unitPrice || 0) : formatCurrency(grandchild.unitPrice || 0)}
                          onChange={(e) => updateItemUnitPrice(grandchild.id, e.target.value, grandchild.currency || 'KRW', isDirect)}
                          className="flex-1 text-right px-2 py-1 rounded text-sm border border-slate-300 focus:border-indigo-500 outline-none"
                          placeholder="단가"
                          style={{ minWidth: '120px' }}
                        />
                      </div>
                    ) : (
                      <div className="text-right">
                        <div className="font-medium">
                          {grandchild.unitPrice ? (grandchild.currency === 'USD' ? `$${formatNumber(grandchild.unitPrice)}` : `₩${formatCurrency(grandchild.unitPrice)}`) : '-'}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editMode ? (
                      <div className="flex items-center space-x-2">
                        <select
                          value={grandchild.currency || 'KRW'}
                          onChange={(e) => updateItemCurrency(grandchild.id, e.target.value as 'KRW' | 'USD', isDirect)}
                          className="text-xs border border-slate-300 rounded px-1 py-1"
                        >
                          <option value="KRW">₩</option>
                          <option value="USD">$</option>
                        </select>
                        <input
                          type="text"
                          value={grandchild.currency === 'USD' ? formatNumber(grandchild.originalAmount || 0) : formatCurrency(grandchild.amount || 0)}
                          onChange={(e) => updateItemAmount(grandchild.id, e.target.value, grandchild.currency || 'KRW', isDirect)}
                          className="flex-1 text-right px-2 py-1 border border-slate-300 rounded text-sm"
                          placeholder="비용"
                        />
                      </div>
                    ) : (
                      <div className="text-right">
                        <div className="font-medium">
                          {formatAmountByCurrency(grandchild).primary}
                        </div>
                        {formatAmountByCurrency(grandchild).secondary && (
                          <div className="text-xs text-gray-500">
                            {formatAmountByCurrency(grandchild).secondary}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-slate-600">
                    <span className="font-medium text-green-700">
                      ${formatNumber(usdAmount)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-slate-600">{itemPercentage}</td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={grandchild.note || ''}
                      onChange={(e) => updateItem(grandchild.id, 'note', e.target.value, isDirect)}
                      className={`w-full text-left px-3 py-2 rounded text-sm ${!editMode ? 'bg-transparent cursor-default border-transparent' : 'bg-white border border-slate-300 focus:border-indigo-500 outline-none'}`}
                      readOnly={!editMode}
                      placeholder={editMode ? "비고" : ""}
                    />
                  </td>
                </tr>
              );
            });
            
          } else {
            // Level 2 일반 항목 (서브카테고리가 아닌 경우)
            const itemPercentage = total > 0 ? (Number(child.amount || 0) / total * 100).toFixed(1) + '%' : '0.0%';
            const usdAmount = child.amount / FIXED_EXCHANGE_RATE;
            
            rows.push(
              <tr key={child.id} className="border-b border-slate-100 bg-slate-25">
                <td className="px-4 py-3 text-center border-l-4 border-green-500 hidden">
                  <span className="text-sm font-medium text-slate-600">
                    {`${itemIndex + 1}.${childIndex + 1}`}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <span className="text-slate-400 mr-2">└</span>
                    <input
                      type="text"
                      value={child.category}
                      onChange={(e) => updateItem(child.id, 'category', e.target.value, isDirect)}
                      className={`flex-1 text-left px-3 py-2 rounded text-sm ${!editMode ? 'bg-transparent cursor-default border-transparent' : validationErrors.has(child.id) ? 'bg-white border border-red-500 focus:border-red-600 outline-none' : 'bg-white border border-slate-300 focus:border-indigo-500 outline-none'}`}
                      readOnly={!editMode}
                      placeholder={editMode ? "항목명을 입력하세요" : ""}
                      data-item-id={child.id}
                    />
                    {editMode && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => addChildItem(item.id, isDirect ? 'direct' : 'indirect', false)}
                          className="text-green-600 hover:text-green-800 w-6 h-6 flex items-center justify-center rounded hover:bg-green-50"
                          title={`"${item.category}" 카테고리에 항목 추가`}
                        >
                          +
                        </button>
                        <button
                          onClick={() => deleteRow(child.id, isDirect)}
                          className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 w-7 h-7 flex items-center justify-center rounded"
                          title={`"${child.category}" 항목 삭제`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </td>
                {/* 수량 열 */}
                <td className="px-4 py-3 text-center">
                  {editMode ? (
                    <input
                      type="number"
                      value={child.quantity || ''}
                      onChange={(e) => updateItem(child.id, 'quantity', parseInt(e.target.value) || 0, isDirect)}
                      className="w-16 text-center px-2 py-1 rounded text-sm border border-slate-300 focus:border-indigo-500 outline-none"
                      placeholder="1"
                      min="0"
                    />
                  ) : (
                    <span className="text-sm font-medium">{child.quantity || '-'}</span>
                  )}
                </td>
                {/* 단가 열 */}
                <td className="px-4 py-3">
                  {editMode ? (
                    <div className="flex items-center space-x-1">
                      <select
                        value={child.currency || 'KRW'}
                        onChange={(e) => updateItemCurrency(child.id, e.target.value as 'KRW' | 'USD', isDirect)}
                        className="text-xs border rounded px-1 py-1"
                      >
                        <option value="KRW">₩</option>
                        <option value="USD">$</option>
                      </select>
                      <input
                        type="text"
                        value={child.currency === 'USD' ? formatNumber(child.unitPrice || 0) : formatCurrency(child.unitPrice || 0)}
                        onChange={(e) => updateItemUnitPrice(child.id, e.target.value, child.currency || 'KRW', isDirect)}
                        className="flex-1 text-right px-2 py-1 rounded text-sm border border-slate-300 focus:border-indigo-500 outline-none"
                        placeholder="단가"
                        style={{ minWidth: '120px' }}
                      />
                    </div>
                  ) : (
                    <div className="text-right">
                      <div className="font-medium">
                        {child.unitPrice ? (child.currency === 'USD' ? `$${formatNumber(child.unitPrice)}` : `₩${formatCurrency(child.unitPrice)}`) : '-'}
                      </div>
                    </div>
                  )}
                </td>
                {/* 총액(비용) 열 */}
                <td className="px-4 py-3">
                  {editMode ? (
                    <div className="text-right">
                      <div className="font-medium text-indigo-600">
                        {child.quantity !== undefined && child.unitPrice !== undefined ?
                          `₩${formatCurrency(child.quantity * child.unitPrice * (child.currency === 'USD' ? FIXED_EXCHANGE_RATE : 1))}`
                          : (child.currency === 'USD' ? `₩${formatCurrency(child.amount)}` : `₩${formatCurrency(child.originalAmount || child.amount)}`)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-right">
                      <div className="font-medium">
                        {formatAmountByCurrency(child).primary}
                      </div>
                      {formatAmountByCurrency(child).secondary && (
                        <div className="text-xs text-gray-500">
                          {formatAmountByCurrency(child).secondary}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-sm text-slate-600">
                  <span className="font-medium text-green-700">
                    ${formatNumber(usdAmount)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-sm font-medium text-slate-600">{itemPercentage}</td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={child.note || ''}
                    onChange={(e) => updateItem(child.id, 'note', e.target.value, isDirect)}
                    className={`w-full text-left px-3 py-2 rounded text-sm ${!editMode ? 'bg-transparent cursor-default border-transparent' : 'bg-white border border-slate-300 focus:border-indigo-500 outline-none'}`}
                    readOnly={!editMode}
                    placeholder={editMode ? "비고" : ""}
                  />
                </td>
              </tr>
            );
          }
        });
      } else {
        // 일반 항목 (카테고리가 아니고 children이 없는 경우)
        const itemPercentage = total > 0 ? (Number(item.amount || 0) / total * 100).toFixed(1) + '%' : '0.0%';
        const usdAmount = item.amount / FIXED_EXCHANGE_RATE;
        
        rows.push(
          <tr key={item.id} className="border-b border-slate-200">
            <td className="px-4 py-3 text-center border-l-4 border-blue-500 hidden">
              <span className="text-sm font-medium text-slate-600">
                {globalIndex + 1}
              </span>
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center">
                <input
                  type="text"
                  value={item.category}
                  onChange={(e) => updateItem(item.id, 'category', e.target.value, isDirect)}
                  className={`flex-1 text-left px-3 py-2 rounded text-sm ${!editMode ? 'bg-transparent cursor-default border-transparent' : validationErrors.has(item.id) ? 'bg-white border border-red-500 focus:border-red-600 outline-none' : 'bg-white border border-slate-300 focus:border-indigo-500 outline-none'}`}
                  readOnly={!editMode}
                  placeholder={editMode ? "항목명을 입력하세요" : ""}
                  data-item-id={item.id}
                />
                {editMode && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => addRow(isDirect ? 'direct' : 'indirect', true)}
                      className="text-green-600 hover:text-green-800 w-6 h-6 flex items-center justify-center rounded hover:bg-green-50"
                      title="카테고리 추가"
                    >
                      +
                    </button>
                    <button
                      onClick={() => deleteRow(item.id, isDirect)}
                      className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 w-7 h-7 flex items-center justify-center rounded"
                      title={`"${item.category}" 항목 삭제`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </td>
            {/* 수량 열 */}
            <td className="px-4 py-3 text-center">
              {editMode ? (
                <input
                  type="number"
                  value={item.quantity || ''}
                  onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0, isDirect)}
                  className="w-16 text-center px-2 py-1 rounded text-sm border border-slate-300 focus:border-indigo-500 outline-none"
                  placeholder="1"
                  min="0"
                />
              ) : (
                <span className="text-sm font-medium">{item.quantity || '-'}</span>
              )}
            </td>
            {/* 단가 열 */}
            <td className="px-4 py-3">
              {editMode ? (
                <div className="flex items-center space-x-1">
                  <select
                    value={item.currency || 'KRW'}
                    onChange={(e) => updateItemCurrency(item.id, e.target.value as 'KRW' | 'USD', isDirect)}
                    className="text-xs border rounded px-1 py-1"
                  >
                    <option value="KRW">₩</option>
                    <option value="USD">$</option>
                  </select>
                  <input
                    type="text"
                    value={item.currency === 'USD' ? formatNumber(item.unitPrice || 0) : formatCurrency(item.unitPrice || 0)}
                    onChange={(e) => updateItemUnitPrice(item.id, e.target.value, item.currency || 'KRW', isDirect)}
                    className="flex-1 text-right px-2 py-1 rounded text-sm border border-slate-300 focus:border-indigo-500 outline-none"
                    placeholder="단가"
                    style={{ minWidth: '120px' }}
                  />
                </div>
              ) : (
                <div className="text-right">
                  <div className="font-medium">
                    {item.unitPrice ? (item.currency === 'USD' ? `$${formatNumber(item.unitPrice)}` : `₩${formatCurrency(item.unitPrice)}`) : '-'}
                  </div>
                </div>
              )}
            </td>
            {/* 총액(비용) 열 */}
            <td className="px-4 py-3">
              {editMode ? (
                <div className="text-right">
                  <div className="font-medium text-indigo-600">
                    {item.quantity !== undefined && item.unitPrice !== undefined ?
                      `₩${formatCurrency(item.quantity * item.unitPrice * (item.currency === 'USD' ? FIXED_EXCHANGE_RATE : 1))}`
                      : (item.currency === 'USD' ? `₩${formatCurrency(item.amount)}` : `₩${formatCurrency(item.originalAmount || item.amount)}`)}
                  </div>
                </div>
              ) : (
                <div className="text-right">
                  <div className="font-medium">
                    {formatAmountByCurrency(item).primary}
                  </div>
                  {formatAmountByCurrency(item).secondary && (
                    <div className="text-xs text-gray-500">
                      {formatAmountByCurrency(item).secondary}
                    </div>
                  )}
                </div>
              )}
            </td>
            <td className="px-4 py-3 text-right text-sm text-slate-600">
              <span className="font-medium text-green-700">
                ${formatNumber(usdAmount)}
              </span>
            </td>
            <td className="px-4 py-3 text-right text-sm font-medium text-slate-600">{itemPercentage}</td>
            <td className="px-4 py-3">
              <input
                type="text"
                value={item.note || ''}
                onChange={(e) => updateItem(item.id, 'note', e.target.value, isDirect)}
                className={`w-full text-left px-3 py-2 rounded text-sm ${!editMode ? 'bg-transparent cursor-default border-transparent' : 'bg-white border border-slate-300 focus:border-indigo-500 outline-none'}`}
                readOnly={!editMode}
                placeholder={editMode ? "비고" : ""}
              />
            </td>
          </tr>
        );
        globalIndex++;
      }
    });

    return rows;
  };

  const renderHeaderRow = (title: string, type: 'indirect' | 'direct') => {
  // 해당 타입의 총합 계산 (공통 함수 사용)
  const items = type === 'direct' ? directItems : indirectItems;
  const subtotal = calculateSubtotal(items);

  return (
    <tr className="bg-slate-100 font-bold">
      <td className="px-4 py-3 hidden"></td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">{title}</span>
          {editMode && (
            <button
              onClick={() => addRow(type, true)}
              className="text-indigo-600 hover:text-indigo-800 text-lg font-bold w-7 h-7 flex items-center justify-center rounded hover:bg-indigo-50"
              title="카테고리 추가"
            >
              +
            </button>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        {/* 소계는 수량 없음 */}
        <span className="text-slate-400">-</span>
      </td>
      <td className="px-4 py-3 text-center">
        {/* 소계는 단가 없음 */}
        <span className="text-slate-400">-</span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm font-bold text-slate-700">
          {formatCurrency(subtotal)}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm font-bold text-slate-600">
          ${formatNumber(subtotal / FIXED_EXCHANGE_RATE)}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm font-bold text-slate-700">
          {total > 0 ? ((subtotal / total * 100).toFixed(1) + '%') : '0.0%'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-slate-600">소계</span>
      </td>
    </tr>
  );
};

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ease-in-out ${
            isAnimating ? 'bg-opacity-40' : 'bg-opacity-0'
          }`}
          onClick={handleClose}
        ></div>
        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <div className={`w-screen max-w-7xl transform transition-transform duration-300 ease-in-out ${
            isAnimating ? 'translate-x-0' : 'translate-x-full'
          }`}>
            <div className="h-full flex flex-col bg-white shadow-xl">
              <header className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-lg">2025년 {localData.month}월 OPEX 상세</h3>
                  </div>
                  <button
                    onClick={handleClose}
                    className="ml-3 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <span className="sr-only">닫기</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-5 gap-3">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">기준 직원수</p>
                    <p className="font-semibold text-indigo-600">{localData.employeeCount} 명</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">USD/KRW 환율</p>
                    <p className="font-semibold">{formatCurrency(FIXED_EXCHANGE_RATE)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Indirect opex</p>
                    <div className="font-semibold">
                      <div>{formatTotalWithCurrencies(indirectItems).primary}</div>
                      {formatTotalWithCurrencies(indirectItems).secondary && (
                        <div className="text-xs text-gray-500 mt-1">
                          {formatTotalWithCurrencies(indirectItems).secondary}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">direct opex</p>
                    <div className="font-semibold">
                      <div>{formatTotalWithCurrencies(directItems).primary}</div>
                      {formatTotalWithCurrencies(directItems).secondary && (
                        <div className="text-xs text-gray-500 mt-1">
                          {formatTotalWithCurrencies(directItems).secondary}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">OPEX 합계</p>
                    <div className="font-bold text-indigo-600">
                      <div>₩{formatCurrency(total)}</div>
                      {(() => {
                        const allItems = [...indirectItems, ...directItems];
                        const totalDisplay = formatTotalWithCurrencies(allItems);
                        return totalDisplay.secondary ? (
                          <div className="text-sm text-indigo-500 mt-1">
                            {totalDisplay.secondary}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                  {!editMode ? (
                    <button
                      onClick={handleEdit}
                      className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-100"
                    >
                      수정
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                        title="저장"
                      >
                        저장
                      </button>
                      {localData.confirmed ? (
                        <button
                          onClick={handleUnconfirm}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700"
                        >
                          확정해제
                        </button>
                      ) : (
                        <button
                          onClick={handleConfirm}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                        >
                          확정
                        </button>
                      )}
                    </>
                  )}
                </div>
              </header>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[250px]" />
                      <col className="w-[100px]" />
                      <col className="w-[160px]" />
                      <col className="w-[180px]" />
                      <col className="w-[120px]" />
                      <col className="w-[90px]" />
                      <col className="w-[200px]" />
                    </colgroup>
                    <thead className="text-xs text-slate-500 uppercase bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-center hidden">NO</th>
                        <th className="px-4 py-3 text-left">항목</th>
                        <th className="px-4 py-3 text-center">수량</th>
                        <th className="px-4 py-3 text-right">단가</th>
                        <th className="px-4 py-3 text-right">비용 (원)</th>
                        <th className="px-4 py-3 text-right">달러 환산</th>
                        <th className="px-4 py-3 text-right">비중</th>
                        <th className="px-4 py-3 text-left">비고</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderHeaderRow('Indirect Opex', 'indirect')}
                      {renderHierarchicalItems(indirectItems, false)}
                      {renderHeaderRow('Direct Opex', 'direct')}
                      {renderHierarchicalItems(directItems, true)}
                      <tr className="bg-slate-200 font-bold text-base border-t-2 border-slate-300">
                        <td className="px-4 py-4 hidden"></td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-bold text-slate-800">Monthly Total Expense</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {/* 합계는 수량 없음 */}
                          <span className="text-slate-400">-</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {/* 합계는 단가 없음 */}
                          <span className="text-slate-400">-</span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className="font-bold text-indigo-600 text-base">
                            {formatCurrency(total)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right font-bold text-green-700">
                          <span>
                            ${new Intl.NumberFormat('en-US').format(Math.round(total / FIXED_EXCHANGE_RATE))}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right font-bold text-slate-800">100%</td>
                        <td className="px-4 py-4"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpexDetailModal;